/**
 * Inhalte der öffentlichen Themenseiten. Reine Daten, keine Geschäftslogik.
 * Wird sowohl von der React-Seite als auch vom Build-Skript
 * scripts/generate-static-routes.mjs gelesen, damit Text und statisches HTML
 * identisch bleiben.
 */

export interface SeoSection {
  h2: string;
  /** Kurze, zitierfähige Antwort direkt unter der Frage. */
  answer?: string;
  paragraphs?: string[];
  bullets?: string[];
  /** Kontextueller interner Link am Ende des Abschnitts. */
  link?: { text: string; to: string; label: string };
}

export interface SeoLink {
  to: string;
  label: string;
}

export interface SeoImage {
  src: string;
  alt: string;
  width: number;
  height: number;
}

export interface SeoPage {
  slug: string;
  /** Nicht Teil der Indexierungs-Whitelist: noindex,follow (bleibt verlinkt und erreichbar). */
  noindex?: boolean;
  /** Optionale Zwischenstufe für Breadcrumb, z. B. Liftarten oder Liftservice. */
  parent?: SeoLink;
  title: string;
  description: string;
  h1: string;
  intro: string;
  /** Handlungsaufruf passend zur Suchintention. */
  cta: string;
  bullets: string[];
  image?: SeoImage;
  sections: SeoSection[];
  faq: { q: string; a: string }[];
  related: SeoLink[];
}

const TRUST_BULLETS = [
  "Kostenlos und unverbindlich für Kunden",
  "Direkter Kontakt durch registrierte Liftanbieter",
  "Fokus Schweiz, für private und gewerbliche Projekte",
];

export const SEO_PAGES: SeoPage[] = [
  {
    slug: "/liftbau-schweiz",
    title: "Liftbau Schweiz: passende Liftanbieter finden | Liftblick",
    description:
      "Liftbau in der Schweiz: Was zum Liftbau gehört, welche Angaben Anbieter brauchen und wie Sie passende Liftfirmen für Neubau, Nachrüstung oder Ersatz finden.",
    h1: "Liftbau in der Schweiz: passende Anbieter finden",
    intro:
      "Ob Personenaufzug im Neubau, Lift im Mehrfamilienhaus oder nachträglicher Einbau im Bestand: Hier erfahren Sie, was zum Liftbau gehört und welche Angaben Liftanbieter für eine belastbare Beurteilung benötigen.",
    cta: "Liftprojekt erfassen",
    bullets: TRUST_BULLETS,
    image: {
      src: "/liftbau-mehrfamilienhaus-schweiz.webp",
      alt: "Aufzugsschacht mit Glasfront in einem Schweizer Mehrfamilienhaus",
      width: 1280,
      height: 800,
    },
    sections: [
      {
        h2: "Was gehört zum Liftbau?",
        answer:
          "Zum Liftbau gehören Planung, Schacht, Anlage und Inbetriebnahme: die Abklärung von Gebäude und Nutzung, die bauliche Vorbereitung von Schacht und Grube, Lieferung und Montage der Anlage, Steuerung und Sicherheitstechnik sowie Abnahme und Wartung.",
        paragraphs: [
          "Ein Liftprojekt beginnt nicht mit der Anlage, sondern mit dem Gebäude. Geschosszahl, Nutzung, Platzverhältnisse und die Frage, ob ein Schacht vorhanden ist, bestimmen, welche Lösungen überhaupt möglich sind. Erst danach lässt sich sinnvoll über Liftart, Tragkraft und Ausstattung sprechen.",
          "Liftblick.ch baut keine Lifte und erstellt keine Angebote. Sie erfassen Ihr Projekt online, registrierte Liftanbieter sehen die passende Anfrage im geschützten Portal und melden sich direkt bei Ihnen.",
        ],
      },
      {
        h2: "Personenaufzug im Neubau",
        paragraphs: [
          "Im Neubau wird der Schacht Teil der Tragstruktur. Wer Liftart, Kabinenmass, Türbreite und Tragkraft früh festlegt, vermeidet spätere Anpassungen an Grundriss, Fundament und Erschliessung. Für Wohnbauten sind Barrierefreiheit und rollstuhlgängige Kabinenmasse in der Regel die bestimmenden Vorgaben.",
        ],
      },
      {
        h2: "Lift im Mehrfamilienhaus",
        paragraphs: [
          "In Mehrfamilienhäusern geht es meist um Erschliessung aller Wohngeschosse, Zugang zu Keller und Waschraum sowie um einen Lift, der sich im Betrieb ruhig verhält. Bei Sanierungen kommt dazu, wie lange die Anlage stillstehen darf und wie die Bewohnerschaft in dieser Zeit erschlossen wird.",
        ],
      },
      {
        h2: "Lift im Einfamilienhaus",
        paragraphs: [
          "Für Einfamilienhäuser kommen kompakte Homelifte infrage, oft mit kleiner Grube und geringer Schachtkopfhöhe. Entscheidend sind die verfügbare Grundfläche, die Deckendurchbrüche und die Frage, ob der Lift innen oder als Anbau an die Fassade geführt wird.",
        ],
      },
      {
        h2: "Nachträglicher Lifteinbau",
        answer:
          "Ein nachträglicher Lifteinbau ist in vielen Bestandsbauten möglich, verlangt aber eine Machbarkeitsabklärung vor Ort: Platz im Treppenauge, Anbau an die Fassade oder ein Einbau in bestehende Nebenräume sind die häufigsten Varianten.",
        paragraphs: [
          "Bei der Nachrüstung entscheiden Statik, Brandschutz, Fluchtwege und die Erschliessung der Geschosse. Auch die Bewilligungssituation und bei Miethäusern die Nutzung während der Bauzeit gehören früh auf den Tisch.",
        ],
      },
      {
        h2: "Bestehender oder neuer Schacht",
        bullets: [
          "Bestehender Schacht: Ersatz der Anlage innerhalb der vorhandenen Masse, meist kürzere Bauzeit.",
          "Neuer Schacht innen: Eingriff in Decken und Grundriss, dafür geschützte Lage der Anlage.",
          "Neuer Schacht aussen: Anbau an die Fassade, oft einfacher im bewohnten Zustand, mit gestalterischer und baurechtlicher Abklärung.",
        ],
      },
      {
        h2: "Modernisierung statt Neubau",
        paragraphs: [
          "Ist eine Anlage vorhanden, aber veraltet, ist häufig eine Modernisierung von Steuerung, Antrieb, Türen und Kabine die günstigere Lösung. Was dabei geprüft wird, steht auf der Seite zur Liftmodernisierung.",
        ],
      },
      {
        h2: "Welche Angaben Liftanbieter benötigen",
        bullets: [
          "Standort und Postleitzahl",
          "Gebäudeart und Nutzung",
          "Anzahl der Geschosse und gewünschte Haltestellen",
          "Neubau, Nachrüstung, Ersatz oder Modernisierung",
          "Schachtsituation und verfügbarer Platz",
          "Personen oder Waren, ungefähre Tragkraft",
          "Anforderungen an Barrierefreiheit",
          "gewünschter Zeitraum",
        ],
      },
      {
        h2: "Ablauf einer Liftanfrage",
        paragraphs: [
          "Sie beschreiben Ihr Projekt online. Die Angaben werden strukturiert gespeichert und registrierten Liftanbietern zugänglich gemacht. Interessierte Anbieter kontaktieren Sie direkt, klären die Details und führen bei Bedarf eine Begehung durch. Preise, Termine und Verträge werden ausschliesslich zwischen Ihnen und dem Anbieter vereinbart.",
        ],
      },
    ],
    faq: [
      {
        q: "Baut Liftblick.ch selbst Lifte?",
        a: "Nein. Liftblick.ch ist eine Schweizer Plattform, über die Kunden ihr Liftprojekt erfassen. Planung, Lieferung und Montage übernehmen die registrierten Liftanbieter.",
      },
      {
        q: "Wie lange dauert ein Liftprojekt?",
        a: "Das hängt von Bauart und Bewilligungen ab. Der Ersatz einer Anlage im bestehenden Schacht ist deutlich kürzer als ein neuer Schacht im bewohnten Gebäude. Verbindliche Termine nennt Ihnen der Anbieter nach der Abklärung vor Ort.",
      },
      {
        q: "Brauche ich für den Liftbau eine Bewilligung?",
        a: "Bei Eingriffen in Tragstruktur, Fassade oder Fluchtwege ist in der Regel eine Bewilligung nötig. Die Anbieter kennen die Anforderungen in Ihrer Region und sagen Ihnen, was für Ihr Projekt gilt.",
      },
    ],
    related: [
      { to: "/personenaufzug-schweiz", label: "Personenaufzug in der Schweiz" },
      { to: "/lift-einfamilienhaus-schweiz", label: "Lift im Einfamilienhaus" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
      { to: "/lift-planen", label: "Liftprojekt planen" },
      { to: "/liftanbieter-schweiz", label: "Liftfirmen und Aufzugsanbieter finden" },
      { to: "/liftmodernisierung-schweiz", label: "Liftmodernisierung planen" },
      { to: "/lift-nachtraeglich-einbauen", label: "Lift nachträglich einbauen" },
    ],
  },

  {
    slug: "/aufzug-kosten-schweiz",
    title: "Aufzug Kosten Schweiz: Preise & Kostenfaktoren | Liftblick",
    description:
      "Was kostet ein Aufzug in der Schweiz? Preisspannen aus Schweizer Quellen (Treppenlift ab CHF 6'000, Aufzug CHF 15'000 bis 60'000) und alle Kostenfaktoren.",
    h1: "Was kostet ein Aufzug in der Schweiz?",
    intro:
      "Es gibt keinen Listenpreis für einen Aufzug. Was Ihr Projekt kostet, hängt vor allem vom Gebäude ab. Hier sehen Sie, welche Faktoren den Preis bestimmen und welche Angaben Sie für eine Beurteilung brauchen.",
    cta: "Projekt kostenlos erfassen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Kurze Antwort",
        answer:
          "Ein Aufzugspreis entsteht aus drei Blöcken: der Anlage selbst, den Bauarbeiten am Gebäude und den laufenden Kosten für Wartung und Prüfungen. Weil der bauliche Anteil je nach Gebäude stark schwankt, ist eine seriöse Zahl erst nach einer Abklärung vor Ort möglich.",
        paragraphs: [
          "Liftblick.ch rechnet selbst keine Preise aus. Sie erfassen Ihr Projekt, registrierte Liftanbieter melden sich direkt bei Ihnen und beurteilen Ihre Situation individuell.",
        ],
      },
      {
        h2: "Preisspannen aus Schweizer Quellen",
        answer:
          "Öffentlich genannte Richtwerte: Treppenlift etwa CHF 6'000 bis 40'000, Aufzug im Wohnhaus etwa CHF 15'000 bis 60'000, nachträglicher Lifteinbau im Mehrfamilienhaus rund CHF 100'000. Ihr Projekt kann deutlich darüber oder darunter liegen.",
        bullets: [
          "Treppenlift gerade Treppe ab rund CHF 6'000, Plattformlift für kurvige Treppen bis rund CHF 17'000 (Quelle: Rigert, Treppenlift-Vergleich)",
          "Treppenlift insgesamt rund CHF 5'800 bis 40'000 je nach Treppe und Modell (Quelle: handwerker-kosten.ch)",
          "Aufzüge rund CHF 15'000 bis 60'000 (Quelle: Daibau.ch, Kostenübersicht Aufzüge)",
          "Lifteinbau im bestehenden Mehrfamilienhaus rund CHF 100'000 (Quelle: Casafair)",
        ],
        paragraphs: [
          "Diese Zahlen stammen von Dritten und sind keine Offerte von Liftblick.ch oder den Anbietern. Sie dienen nur zur groben Einordnung. Stand der Angaben: Oktober 2026.",
        ],
      },
      {
        h2: "Kostenfaktoren im Überblick",
        bullets: [
          "Liftart: Personenaufzug, Homelift, Treppenlift, Plattformlift, Warenlift",
          "Gebäudeart: Einfamilienhaus, Mehrfamilienhaus, Gewerbe, öffentlicher Bau",
          "Anzahl Stockwerke und Haltestellen",
          "bestehender Schacht oder neuer Schacht innen oder aussen",
          "Tragkraft und Kabinenmass",
          "Anforderungen an Barrierefreiheit",
          "Ausstattung: Türen, Kabine, Steuerung, Bedienelemente",
          "Neubau, Nachrüstung im Bestand oder Ersatz einer Anlage",
          "Modernisierung einzelner Komponenten statt Vollersatz",
          "Region, Zugänglichkeit der Baustelle, Montagesituation",
          "Wartung, Prüfungen und Notrufaufschaltung im Betrieb",
        ],
      },
      {
        h2: "Warum die Bauarbeiten so stark ins Gewicht fallen",
        paragraphs: [
          "Die Anlage ist bei vielen Projekten der besser planbare Teil. Teuer wird, was am Gebäude passiert: Deckendurchbrüche, Grube, Verstärkungen, Anpassungen an Treppe und Fluchtweg, Elektroanschluss und Wiederherstellungsarbeiten. Ein Ersatz im bestehenden Schacht liegt deshalb in einer anderen Grössenordnung als ein neuer Schacht in einem bewohnten Altbau.",
        ],
      },
      {
        h2: "Kosten Lift Einfamilienhaus Schweiz",
        answer:
          "Im Einfamilienhaus bestimmen drei Punkte den Preis: die Anzahl der Haltestellen, ob ein Schacht neu erstellt werden muss und ob der Lift innen oder als Anbau geführt wird.",
        paragraphs: [
          "Kompakte Homelifte brauchen wenig Grube und Schachtkopf, was den baulichen Aufwand senkt. Aufwendiger wird es, wenn Decken durchbrochen, Wände verstärkt oder Nebenräume verlegt werden müssen. Wer bereits weiss, wie viele Geschosse erschlossen werden und wo der Lift stehen soll, erhält deutlich präzisere Rückmeldungen.",
        ],
      },
      {
        h2: "Laufende Kosten nicht vergessen",
        paragraphs: [
          "Zum Projekt gehören Wartung, wiederkehrende Prüfungen, Notrufaufschaltung und Strom. Ein Wartungsvertrag ist bei jeder Anlage Teil der Rechnung, unabhängig davon, wer die Anlage geliefert hat. Wie Sie dafür Anbieter finden, steht auf der Seite zu Liftservice und Aufzugswartung.",
        ],
      },
      {
        h2: "Was Sie für eine Beurteilung bereitstellen sollten",
        bullets: [
          "Standort und Gebäudeart",
          "Geschosse und gewünschte Haltestellen",
          "Fotos oder Pläne der Situation, wenn vorhanden",
          "Angaben zur bestehenden Anlage bei Ersatz oder Modernisierung",
          "Nutzung: Personen, Rollstuhl, Waren",
          "Zeitrahmen und Budgetvorstellung",
        ],
      },
      {
        h2: "Jedes Projekt braucht eine eigene Abklärung",
        paragraphs: [
          "Zwei Häuser mit gleicher Geschosszahl können sehr unterschiedliche Kosten verursachen. Deshalb beurteilen die Anbieter Ihre Situation einzeln. Die Preisspannen oben ersetzen keine Offerte.",
        ],
      },
    ],
    faq: [
      {
        q: "Gibt es bei Liftblick.ch einen Preisrechner?",
        a: "Nein. Liftblick.ch ist kein Preisrechner. Auf dieser Seite finden Sie Preisspannen aus Schweizer Quellen zur groben Einordnung. Den verbindlichen Preis nennt Ihnen der Liftanbieter nach der Abklärung.",
      },
      {
        q: "Ist die Anfrage für Kunden kostenpflichtig?",
        a: "Nein. Das Erfassen eines Projekts über Liftblick.ch ist für Kunden kostenlos und unverbindlich.",
      },
      {
        q: "Was ist günstiger: Modernisierung oder neue Anlage?",
        a: "Wenn Schacht und Tragwerk in gutem Zustand sind, ist die Modernisierung von Steuerung, Antrieb, Türen und Kabine meist günstiger. Ist die Anlage am Ende der Lebensdauer, kann ein Ersatz wirtschaftlicher sein. Das beurteilt der Anbieter an der Anlage.",
      },
    ],
    related: [
      { to: "/lift-planen", label: "Liftprojekt planen" },
      { to: "/liftbau-schweiz", label: "Liftbau in der Schweiz" },
      { to: "/lift-einfamilienhaus-schweiz", label: "Lift im Einfamilienhaus" },
      { to: "/homelift-schweiz", label: "Homelift für Wohnhäuser" },
      { to: "/treppenlift-schweiz", label: "Treppenlift Anbieter finden" },
    ],
  },

  {
    slug: "/lift-planen",
    title: "Lift planen in der Schweiz: Ablauf und wichtige Fragen",
    description:
      "Lift oder Aufzug planen: Welche Angaben Sie vor dem Projekt klären sollten, von Gebäudeart und Liftart über Schacht und Barrierefreiheit bis zur Anbieterwahl.",
    h1: "Lift planen: Das sollten Sie vor dem Projekt klären",
    intro:
      "Je klarer Ihre Ausgangslage beschrieben ist, desto belastbarer sind die Rückmeldungen der Liftanbieter. Diese Punkte lohnt es sich vorher zu klären.",
    cta: "Projekt starten",
    bullets: TRUST_BULLETS,
    image: {
      src: "/aufzug-planung-schweiz.webp",
      alt: "Schnittzeichnung eines Aufzugsschachts über drei Geschosse auf einem Arbeitstisch",
      width: 1280,
      height: 800,
    },
    sections: [
      {
        h2: "Was muss ich bei der Planung eines Lifts beachten?",
        answer:
          "Klären Sie zuerst Gebäude, Nutzung und Platz: Wie viele Geschosse sollen erschlossen werden, wer nutzt den Lift, gibt es einen Schacht und wie viel Fläche steht zur Verfügung. Erst danach folgen Liftart, Tragkraft und Ausstattung.",
      },
      {
        h2: "Gebäudeart",
        paragraphs: [
          "Einfamilienhaus, Mehrfamilienhaus, Geschäftshaus oder öffentlicher Bau: Daraus ergeben sich Anforderungen an Kabinenmass, Türen, Brandschutz und Verfügbarkeit. Bei Mietobjekten kommt die Frage dazu, wie das Gebäude während der Bauzeit nutzbar bleibt.",
        ],
      },
      {
        h2: "Liftart",
        bullets: [
          "Personenaufzug für die vollständige Erschliessung eines Gebäudes",
          "Homelift für private Wohnhäuser mit kompaktem Platzbedarf",
          "Treppenlift für die Nachrüstung entlang einer bestehenden Treppe",
          "Plattformlift für rollstuhlgängige Höhenunterschiede",
          "Warenlift oder Lastenaufzug für Gewerbe und Lager",
        ],
      },
      {
        h2: "Etagen und Haltestellen",
        paragraphs: [
          "Notieren Sie, welche Geschosse erschlossen werden sollen, inklusive Keller, Garage und Dachgeschoss. Jede zusätzliche Haltestelle beeinflusst Schachtlänge, Türen und Preis.",
        ],
      },
      {
        h2: "Personen oder Waren",
        paragraphs: [
          "Personenverkehr, Rollstuhl, Kinderwagen, Möbeltransporte oder Warenverkehr führen zu unterschiedlichen Tragkräften und Kabinenmassen. Wer den späteren Alltag beschreibt, erhält passendere Vorschläge als über eine reine Kilogrammangabe.",
        ],
      },
      {
        h2: "Platzverhältnisse und Schacht",
        paragraphs: [
          "Messen Sie die verfügbare Grundfläche, die Geschosshöhen und, falls vorhanden, den Schachtquerschnitt. Wichtig sind auch Grube, Schachtkopfhöhe und die Frage, ob ein Anbau an die Fassade möglich ist. Fotos der Situation helfen den Anbietern mehr als lange Beschreibungen.",
        ],
      },
      {
        h2: "Barrierefreiheit",
        paragraphs: [
          "Soll der Lift rollstuhlgängig sein, gelten Mindestmasse für Kabine, Türbreite und Bewegungsflächen davor. Prüfen Sie zugleich den Weg vom Hauseingang bis zur Kabine, denn eine Schwelle oder eine einzelne Stufe macht eine barrierefreie Lösung unvollständig.",
        ],
      },
      {
        h2: "Nutzungsprofil und Zeitraum",
        paragraphs: [
          "Häufigkeit der Nutzung, Betriebszeiten und die Frage, wie lange ein Stillstand tragbar ist, beeinflussen Technikwahl und Wartungsumfang. Halten Sie zudem fest, ab wann das Projekt starten soll und ob es bauliche Termine gibt, an die es gebunden ist.",
        ],
      },
      {
        h2: "Budgetrahmen und Anbieterwahl",
        paragraphs: [
          "Ein Budgetrahmen ist kein Preis, hilft dem Anbieter aber, realistische Varianten zu zeigen. Achten Sie bei der Anbieterwahl darauf, wie sorgfältig Ihre Situation aufgenommen wird, was der Wartungsvertrag umfasst und wie Ersatzteilversorgung und Servicezeiten geregelt sind.",
        ],
      },
      {
        h2: "Technische Angaben, die Sie sammeln können",
        bullets: [
          "Geschosshöhen und Anzahl Haltestellen",
          "Schachtmasse, Grube, Schachtkopf",
          "vorhandener Elektroanschluss",
          "bestehende Anlage: Baujahr, Hersteller, Tragkraft",
          "Pläne, Fotos, Grundrisse",
        ],
      },
    ],
    faq: [
      {
        q: "Muss ich vor der Anfrage schon alles wissen?",
        a: "Nein. Erfassen Sie, was bekannt ist. Fehlende technische Angaben klärt der Liftanbieter bei der Abklärung vor Ort.",
      },
      {
        q: "Brauche ich einen Architekten für die Liftplanung?",
        a: "Bei Eingriffen in Tragstruktur oder Fassade ist eine Fachplanung sinnvoll. Bei einem Ersatz im bestehenden Schacht genügt in der Regel die Beurteilung durch die Liftfirma.",
      },
    ],
    related: [
      { to: "/liftbau-schweiz", label: "Liftbau in der Schweiz" },
      { to: "/personenaufzug-schweiz", label: "Personenaufzug in der Schweiz" },
      { to: "/plattformlift-schweiz", label: "Plattformlift und Rollstuhllift" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
      { to: "/liftanbieter-schweiz", label: "Liftfirmen und Aufzugsanbieter finden" },
    ],
  },

  {
    slug: "/treppenlift-schweiz",
    title: "Treppenlift Anbieter Schweiz finden | Liftblick",
    description:
      "Treppenlift Anbieter in der Schweiz finden: gerade und kurvige Treppen, Sitzlifte und Plattformlifte, Innen- und Aussenbereich, Planung und Kostenfaktoren.",
    h1: "Treppenlift Anbieter in der Schweiz finden",
    intro:
      "Ein Treppenlift macht ein bestehendes Haus zugänglich, ohne den Grundriss zu verändern. Erfassen Sie Ihre Treppensituation online, passende Anbieter melden sich direkt bei Ihnen.",
    cta: "Treppenlift-Anfrage erfassen",
    bullets: TRUST_BULLETS,
    image: {
      src: "/treppenlift-wohnhaus.webp",
      alt: "Sitzlift an einer Schiene entlang einer kurvigen Treppe in einem Wohnhaus",
      width: 1280,
      height: 800,
    },
    sections: [
      {
        h2: "Wie finde ich einen Treppenlift-Anbieter in der Schweiz?",
        answer:
          "Beschreiben Sie Ihre Treppe: gerade oder kurvig, Anzahl Geschosse, Treppenbreite, innen oder aussen, und wer den Lift nutzen wird. Mit diesen Angaben können registrierte Anbieter beurteilen, ob ein Sitzlift oder ein Plattformlift passt, und sich direkt bei Ihnen melden.",
      },
      {
        h2: "Gerade Treppen",
        paragraphs: [
          "Bei geraden Treppen läuft die Schiene in einer Linie. Die Montage ist einfacher und schneller, weil keine gebogene Schiene angefertigt werden muss. Wichtig ist die verbleibende Durchgangsbreite, damit die Treppe weiterhin bequem begehbar bleibt.",
        ],
      },
      {
        h2: "Kurvige Treppen",
        paragraphs: [
          "Wendel- und Podesttreppen brauchen eine individuell gebogene Schiene, die nach Aufmass gefertigt wird. Das erhöht Vorlaufzeit und Aufwand, erlaubt aber, mehrere Geschosse ohne Umsteigen zu erschliessen und Kurven eng zu führen.",
        ],
      },
      {
        h2: "Sitzlift oder Plattformlift",
        bullets: [
          "Sitzlift: für Personen, die sich setzen und wieder aufstehen können, mit klappbarem Sitz und Fussbrett.",
          "Plattformlift: für Rollstuhlnutzung, braucht mehr Treppenbreite und mehr Platz an den Haltepunkten.",
          "Hublift: für einzelne Höhenunterschiede, etwa vom Vorplatz zur Haustür.",
        ],
      },
      {
        h2: "Innenbereich und Aussenbereich",
        paragraphs: [
          "Aussen montierte Lifte brauchen wetterfeste Ausführung, geschützte Elektronik und eine Abdeckung im Stillstand. Auch der Untergrund und die Befestigung an Treppe oder Mauer werden vor Ort beurteilt.",
        ],
      },
      {
        h2: "Barrierefreiheit im ganzen Weg denken",
        paragraphs: [
          "Ein Treppenlift löst die Treppe, nicht den ganzen Zugang. Prüfen Sie den Weg vom Parkplatz oder Hauseingang bis zur Wohnung: Schwellen, einzelne Stufen und schmale Türen entscheiden mit darüber, ob eine Lösung im Alltag funktioniert. Reicht ein Treppenlift nicht, ist ein Homelift die Alternative.",
        ],
      },
      {
        h2: "Was kostet ein Treppenlift in der Schweiz?",
        paragraphs: [
          "Öffentlich genannte Richtwerte aus Schweizer Quellen: Treppenlift für eine gerade Treppe ab rund CHF 6'000, Plattformlift für kurvige Treppen bis rund CHF 17'000 (Rigert). Insgesamt liegen Treppenlifte je nach Treppe und Modell bei rund CHF 5'800 bis 40'000 (handwerker-kosten.ch). Den verbindlichen Preis nennt der Anbieter nach dem Aufmass vor Ort.",
        ],
      },
      {
        h2: "Kostenfaktoren beim Treppenlift",
        bullets: [
          "gerade oder kurvige Schiene",
          "Anzahl Geschosse und Länge der Fahrstrecke",
          "Sitzlift oder Plattformlift",
          "innen oder aussen",
          "Treppenbreite und Befestigungssituation",
          "Ausstattung wie Klappsitz, Fernbedienung, Drehsitz",
          "Wartung und Service",
        ],
      },
      {
        h2: "Ablauf der Anfrage",
        paragraphs: [
          "Sie erfassen Treppensituation, Nutzung und Standort online. Registrierte Anbieter sehen die Anfrage im geschützten Portal und melden sich direkt. Das Aufmass erfolgt vor Ort, weil Schienenverlauf und Befestigung nicht aus der Ferne beurteilt werden können.",
        ],
      },
    ],
    faq: [
      {
        q: "Passt ein Treppenlift auf eine schmale Treppe?",
        a: "Oft ja. Entscheidend ist die verbleibende Durchgangsbreite mit hochgeklapptem Sitz. Der Anbieter beurteilt das beim Aufmass vor Ort.",
      },
      {
        q: "Kann ich einen Treppenlift auch für den Aussenbereich anfragen?",
        a: "Ja. Geben Sie an, dass die Montage im Aussenbereich erfolgt, damit die Anbieter eine wetterfeste Ausführung vorsehen.",
      },
      {
        q: "Verkauft Liftblick.ch Treppenlifte?",
        a: "Nein. Liftblick.ch vermittelt keine Produkte und erstellt keine Angebote. Beratung, Aufmass und Angebot kommen vom Liftanbieter.",
      },
    ],
    related: [
      { to: "/plattformlift-schweiz", label: "Plattformlift und Rollstuhllift" },
      { to: "/homelift-schweiz", label: "Homelift für Wohnhäuser" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
      { to: "/lift-planen", label: "Liftprojekt planen" },
    ],
  },

  {
    slug: "/liftservice-schweiz",
    title: "Liftservice Schweiz: Wartung & Servicefirma finden | Liftblick",
    description:
      "Liftservice oder Liftwartung in der Schweiz gesucht? Erfahren Sie mehr über Wartungsverträge, Servicefirmen und Anbieterwechsel. Anfrage kostenlos erfassen.",
    h1: "Liftservice und Aufzugswartung in der Schweiz",
    intro:
      "Sie suchen eine Servicefirma für die Liftwartung, möchten Ihren Wartungsvertrag prüfen oder die Wartungsfirma wechseln? Erfassen Sie Ihr Anliegen kostenlos. Registrierte Liftfirmen können Ihre Anfrage einsehen und Sie direkt kontaktieren.",
    cta: "Liftservice anfragen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Liftservice: Welche Leistungen gehören dazu?",
        answer:
          "Zum Liftservice gehören die regelmässige Wartung, die Kontrolle von Antrieb, Türen, Steuerung und Sicherheitseinrichtungen sowie meist ein Störungsdienst. Der genaue Umfang steht im Wartungsvertrag.",
        paragraphs: [
          "Wie oft gewartet wird, richtet sich nach Liftart und Nutzung. Ob Reparaturen, Ersatzteile oder Einsätze ausserhalb der Bürozeiten enthalten sind, unterscheidet sich von Vertrag zu Vertrag.",
        ],
      },
      {
        h2: "Lift Servicefirma finden",
        paragraphs: [
          "Eine passende Servicefirma kennt Ihre Liftart und ist in Ihrer Region tätig. Über Liftblick erfassen Sie Standort, Anlage und gewünschten Leistungsumfang. Registrierte Liftfirmen sehen die Anfrage und melden sich bei Interesse direkt bei Ihnen. Leistungen und Preise vereinbaren Sie mit der Firma.",
        ],
      },
      {
        h2: "Liftwartung für Mehrfamilienhäuser",
        paragraphs: [
          "Verwaltungen und Eigentümergemeinschaften achten auf planbare Kosten, verständliche Rapporte und eine verlässliche Erreichbarkeit bei Störungen. Hilfreich ist eine Übersicht aller Anlagen mit Standort, Baujahr und Vertragsende.",
        ],
      },
      {
        h2: "Liftwartung für Einfamilienhäuser",
        paragraphs: [
          "Auch ein Homelift, Treppenlift oder Plattformlift im Einfamilienhaus braucht regelmässige Wartung. Der Aufwand ist meist kleiner als bei einem Personenaufzug im Mehrfamilienhaus, die Anforderungen hängen aber vom Modell und von der Nutzung ab.",
        ],
      },
      {
        h2: "Wartungsfirma wechseln",
        paragraphs: [
          "Ein Wechsel ist grundsätzlich möglich, auch bei einer Anlage eines anderen Herstellers. Zu klären sind Kündigungsfrist, Zugang zur Steuerung, Ersatzteile und die Übergabe der Dokumentation. Halten Sie den bestehenden Vertrag bereit.",
        ],
      },
      {
        h2: "Was tun bei wiederkehrenden Liftstörungen?",
        paragraphs: [
          "Häufen sich Störungen, notieren Sie Datum, Stockwerk und Verhalten des Lifts. Besprechen Sie die Liste mit der Wartungsfirma. Bleibt das Problem bestehen, kann eine zweite Einschätzung oder ein Wechsel der Servicefirma sinnvoll sein.",
        ],
        link: {
          text: "Ist Ihr Lift defekt oder kaputt?",
          to: "/liftreparatur-schweiz",
          label: "Erfahren Sie, wie Sie bei einer Liftstörung vorgehen.",
        },
      },
      {
        h2: "Liftservice oder Liftreparatur?",
        answer:
          "Liftservice ist die laufende Wartung im Rahmen eines Vertrags. Eine Liftreparatur behebt eine konkrete Störung oder einen Schaden. Werden mehrere Hauptteile erneuert, spricht man von einer Modernisierung.",
        link: {
          text: "Steht eine grössere Erneuerung an?",
          to: "/liftmodernisierung-schweiz",
          label: "Liftmodernisierung planen",
        },
      },
      {
        h2: "Wartungsvertrag für den Lift prüfen",
        bullets: [
          "Anzahl Wartungsbesuche pro Jahr",
          "enthaltene Arbeiten und ausgenommene Leistungen",
          "Erreichbarkeit bei Störung und Personenbefreiung",
          "Umgang mit Ersatzteilen und Preisanpassungen",
          "Laufzeit und Kündigungsfrist",
        ],
        link: {
          text: "Mehr zu Inhalt, Laufzeit und Kündigung:",
          to: "/lift-abo-schweiz",
          label: "Lift-Abo und Wartungsvertrag",
        },
      },
      {
        h2: "Welche Angaben Sie bereithalten sollten",
        bullets: [
          "Standort und Anzahl Anlagen",
          "Liftart und Tragkraft",
          "Hersteller und Baujahr, wenn bekannt",
          "aktuelle Wartungsfirma und Vertragsende",
          "Störungshäufigkeit der letzten Monate",
          "gewünschter Leistungsumfang",
        ],
      },
    ],
    faq: [
      {
        q: "Führt Liftblick Wartungen durch?",
        a: "Nein. Liftblick wartet keine Anlagen. Die Wartung übernimmt die von Ihnen gewählte Liftfirma.",
      },
      {
        q: "Kann ich mehrere Anlagen gemeinsam erfassen?",
        a: "Ja. Beschreiben Sie den Umfang in Ihrer Anfrage, damit die Firmen das Portfolio als Ganzes beurteilen können.",
      },
      {
        q: "Nennt Liftblick garantierte Reaktionszeiten?",
        a: "Nein. Reaktionszeiten sind Teil des Vertrags, den Sie direkt mit der Wartungsfirma vereinbaren.",
      },
    ],
    related: [
      { to: "/lift-abo-schweiz", label: "Lift-Abo und Wartungsvertrag prüfen" },
      { to: "/liftreparatur-schweiz", label: "Lift defekt oder kaputt?" },
      { to: "/liftmodernisierung-schweiz", label: "Liftmodernisierung planen" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
      { to: "/liftanbieter-schweiz", label: "Liftfirmen und Aufzugsanbieter finden" },
    ],
  },

  {
    slug: "/liftmodernisierung-schweiz",
    title: "Liftmodernisierung Schweiz: passende Anbieter finden | Liftblick",
    description:
      "Liftmodernisierung in der Schweiz: Steuerung, Türen, Kabine, Barrierefreiheit, Energieeffizienz und die Frage Modernisierung oder Ersatz.",
    h1: "Liftmodernisierung in der Schweiz",
    intro:
      "Viele Anlagen laufen technisch noch, entsprechen aber nicht mehr dem heutigen Stand bei Sicherheit, Komfort und Energieverbrauch. Erfassen Sie Ihre Anlage online und lassen Sie den Zustand von Anbietern beurteilen.",
    cta: "Modernisierung anfragen",
    bullets: TRUST_BULLETS,
    image: {
      src: "/liftmodernisierung-schweiz.webp",
      alt: "Aufzugskabine mit erneuerter Steuerung und neuen Bedienelementen",
      width: 1280,
      height: 800,
    },
    sections: [
      {
        h2: "Was ist der Unterschied zwischen Liftmodernisierung und Ersatz?",
        answer:
          "Bei der Modernisierung bleiben Schacht und Tragstruktur bestehen und einzelne Komponenten wie Steuerung, Antrieb, Türen oder Kabine werden erneuert. Beim Ersatz wird die Anlage komplett ausgetauscht, meist wenn mehrere Hauptkomponenten am Ende der Lebensdauer sind oder die Geometrie nicht mehr genügt.",
      },
      {
        h2: "Bestehende Liftanlagen beurteilen",
        paragraphs: [
          "Grundlage ist der Zustand: Baujahr, Betriebsstunden, Störungshäufigkeit, Verfügbarkeit von Ersatzteilen und die Ergebnisse der letzten Kontrollen. Daraus ergibt sich, ob einzelne Eingriffe genügen oder ein Ersatz wirtschaftlicher ist.",
        ],
      },
      {
        h2: "Steuerung",
        paragraphs: [
          "Eine neue Steuerung verbessert Fahrverhalten, Diagnose und Ersatzteilsituation. Sie ist häufig der erste Schritt, weil alte Steuerungen die Verfügbarkeit der ganzen Anlage begrenzen.",
        ],
      },
      {
        h2: "Türen",
        paragraphs: [
          "Türen sind die häufigste Störungsquelle. Neue Antriebe, Lichtschranken und Schwellen erhöhen die Zuverlässigkeit und die Sicherheit beim Ein- und Aussteigen deutlich.",
        ],
      },
      {
        h2: "Kabine",
        paragraphs: [
          "Bei der Kabine geht es um Boden, Wände, Beleuchtung, Handlauf, Spiegel und Bedienelemente. Neben dem Erscheinungsbild verbessert das die Orientierung und die Nutzbarkeit für Menschen mit Einschränkungen.",
        ],
      },
      {
        h2: "Barrierefreiheit",
        bullets: [
          "Bedienelemente in erreichbarer Höhe und mit tastbaren Zeichen",
          "akustische und optische Anzeigen",
          "ausreichende Türbreite und Haltezeiten",
          "niveaugleicher Übergang zwischen Kabine und Geschoss",
        ],
      },
      {
        h2: "Energieeffizienz",
        paragraphs: [
          "Moderne Antriebe, Standby-Schaltungen und LED-Beleuchtung senken den Verbrauch, besonders bei Anlagen mit langen Stillstandzeiten. Bei häufig genutzten Liften kann eine Rückspeisung sinnvoll sein.",
        ],
      },
      {
        h2: "Sicherheit",
        paragraphs: [
          "Nachrüstungen betreffen häufig Türschutz, Notrufaufschaltung, Notbefreiung, Schachtabschlüsse und die Absicherung des Kabinendachs. Was in Ihrem Fall angezeigt ist, beurteilt die Fachfirma an der Anlage.",
        ],
      },
      {
        h2: "Planung und Anbieterwahl",
        paragraphs: [
          "Klären Sie vorab, wie lange die Anlage stillstehen darf, wie das Gebäude in dieser Zeit erschlossen wird und in welchen Etappen gearbeitet wird. Bei der Anbieterwahl zählen die Sorgfalt der Zustandsaufnahme, die Ersatzteilversorgung und der Wartungsvertrag nach dem Umbau.",
        ],
      },
    ],
    faq: [
      {
        q: "Wie lange steht der Lift bei einer Modernisierung still?",
        a: "Das hängt vom Umfang ab. Der Austausch einer Steuerung dauert deutlich kürzer als eine Erneuerung von Antrieb, Türen und Kabine. Verbindliche Angaben macht der Anbieter nach der Zustandsaufnahme.",
      },
      {
        q: "Kann eine fremde Firma meine Anlage modernisieren?",
        a: "In vielen Fällen ja. Massgebend sind Zugang zur Steuerung und Verfügbarkeit der Komponenten. Das beurteilt die Firma an der Anlage.",
      },
    ],
    related: [
      { to: "/liftservice-schweiz", label: "Liftservice anfragen" },
      { to: "/liftreparatur-schweiz", label: "Störungen und Reparaturen" },
      { to: "/lift-abo-schweiz", label: "Lift-Abo und Wartungsvertrag" },
      { to: "/liftbau-schweiz", label: "Liftbau in der Schweiz" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
    ],
  },

  {
    slug: "/liftanbieter-schweiz",
    title: "Liftfirmen Schweiz: Aufzugsanbieter finden | Liftblick",
    description:
      "Welche Arten von Liftfirmen und Aufzugsanbietern es in der Schweiz gibt, wie Sie Anbieter nach Projektart und Region finden und worauf Sie bei der Auswahl achten.",
    h1: "Liftfirmen und Aufzugsanbieter in der Schweiz",
    intro:
      "Liftbau, Wartung, Reparatur, Modernisierung, Homelift, Treppenlift oder Plattformlift: Nicht jede Liftfirma deckt alle Bereiche ab. Hier sehen Sie, welche Anbietertypen es gibt und wie Sie den passenden für Ihre Projektart finden.",
    cta: "Liftanfrage erfassen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Wie funktioniert die Anbietersuche?",
        answer:
          "Sie beschreiben Liftart, Gebäude, Standort und Zeitraum. Die Anfrage wird strukturiert gespeichert und registrierten Liftanbietern zugänglich gemacht. Interessierte Anbieter melden sich direkt bei Ihnen, Preise und technische Details klären Sie mit ihnen.",
      },
      {
        h2: "Welche Projekte Sie erfassen können",
        bullets: [
          "Personenaufzug für Neubau oder Ersatz",
          "Homelift für private Wohnhäuser",
          "Treppenlift und Plattformlift für Barrierefreiheit",
          "Warenlift und Lastenaufzug für Gewerbe",
          "Aufzugswartung, Service oder Anbieterwechsel",
          "Liftmodernisierung bestehender Anlagen",
          "nachträglicher Lifteinbau im Bestand",
        ],
      },
      {
        h2: "Worauf Sie bei der Anbieterwahl achten können",
        bullets: [
          "Wie sorgfältig wird Ihre Situation vor Ort aufgenommen?",
          "Was umfasst der Wartungsvertrag nach der Montage?",
          "Wie ist die Ersatzteilversorgung über die Lebensdauer geregelt?",
          "Werden Varianten gezeigt und Entscheidungen begründet?",
          "Sind Leistungen, Termine und Abgrenzungen im Angebot nachvollziehbar?",
        ],
      },
      {
        h2: "Was Liftblick.ch nicht macht",
        paragraphs: [
          "Liftblick.ch verkauft keine Lifte, montiert nicht, wartet nicht und erstellt keine Angebote. Es gibt keine automatischen Preise und keine garantierten Offerten. Die Plattform sorgt dafür, dass Ihre Angaben vollständig bei den passenden Anbietern landen.",
        ],
      },
      {
        h2: "Welche Arten von Liftanbietern es gibt",
        answer:
          "Der Markt teilt sich grob in Aufzugsbauer für Neubau und Ersatz, Servicefirmen für Wartung und Reparatur, Modernisierungsspezialisten sowie Anbieter, die auf Homelifte, Treppenlifte und Plattformlifte ausgerichtet sind. Viele Firmen decken mehrere Bereiche ab, aber selten alle gleich stark.",
        bullets: [
          "Liftbau und Neuanlagen: Personenaufzüge für Neubau, Ersatz und Nachrüstung.",
          "Liftservice und Wartung: Wartungsverträge, Kontrollen und Störungsdienst.",
          "Liftreparatur: Behebung konkreter Störungen an bestehenden Anlagen.",
          "Liftmodernisierung: Erneuerung von Steuerung, Antrieb, Türen und Kabine.",
          "Homelift-Anbieter: kompakte Anlagen für private Wohnhäuser.",
          "Treppenlift- und Plattformlift-Anbieter: Nachrüstung für Barrierefreiheit.",
        ],
      },
      {
        h2: "Herstellergebundene und unabhängige Firmen",
        paragraphs: [
          "Ein Teil der Firmen vertreibt und betreut die Anlagen eines Herstellers, andere arbeiten herstellerunabhängig und betreuen gemischte Bestände. Für Neuanlagen ist die Produktpalette entscheidend, für Wartung und Reparatur eher der Zugang zu Steuerung und Ersatzteilen Ihrer bestehenden Anlage.",
        ],
      },
      {
        h2: "Anbieter nach Projektart finden",
        paragraphs: [
          "Beschreiben Sie zuerst die Projektart, nicht die Firma: Neubau, Ersatz, Nachrüstung, Wartungsvertrag, Reparatur oder Modernisierung. Daraus ergibt sich, welche Anbietertypen überhaupt infrage kommen, und Ihre Angaben landen bei den Firmen, die diesen Bereich abdecken.",
        ],
      },
      {
        h2: "Regionale Anbieter",
        paragraphs: [
          "Bei Wartung und Reparatur zählt die Nähe: Anfahrtswege bestimmen mit, wie schnell jemand bei einer Störung vor Ort ist. Deshalb gehören Standort und Postleitzahl in jede Anfrage. Bei Neuanlagen ist der Einsatzradius meist grösser, die Montage wird ohnehin geplant.",
        ],
      },
      {
        h2: "Welche Informationen ein Anbieter benötigt",
        bullets: [
          "Standort und Postleitzahl",
          "Gebäudeart und Nutzung",
          "Projektart: Neubau, Ersatz, Nachrüstung, Wartung, Reparatur, Modernisierung",
          "Liftart und Anzahl Haltestellen",
          "bestehende Anlage: Hersteller und Baujahr, wenn bekannt",
          "Anforderungen an Barrierefreiheit",
          "gewünschter Zeitraum",
        ],
      },
    ],
    faq: [
      {
        q: "Ist Liftblick.ch ein Preisvergleich?",
        a: "Nein. Liftblick.ch ist kein automatischer Preisvergleich und kein Preisrechner. Preise und technische Details werden direkt zwischen Kunde und Anbieter besprochen.",
      },
      {
        q: "Kostet die Anfrage für Kunden etwas?",
        a: "Nein. Das Erfassen einer Anfrage ist für Kunden kostenlos und unverbindlich.",
      },
      {
        q: "Wer sieht meine Anfrage?",
        a: "Ihre Anfrage wird im Liftblick-System gespeichert und registrierten Liftanbietern zugänglich gemacht, die zur Bearbeitung Ihrer Anfrage zugelassen sind.",
      },
    ],
    related: [
      { to: "/liftbau-schweiz", label: "Liftbau in der Schweiz" },
      { to: "/personenaufzug-schweiz", label: "Personenaufzug in der Schweiz" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
      { to: "/lift-planen", label: "Liftprojekt planen" },
      { to: "/liftservice-schweiz", label: "Liftservice anfragen" },
      { to: "/liftreparatur-schweiz", label: "Lift reparieren lassen" },
      { to: "/lift-abo-schweiz", label: "Lift-Abo und Wartungsvertrag" },
    ],
  },

  {
    slug: "/homelift-schweiz",
    title: "Homelift Anbieter Schweiz finden | Liftblick",
    description:
      "Homelift in der Schweiz: kompakte Lifte für Einfamilienhäuser, Platzbedarf, Nachrüstung und Kostenfaktoren. Projekt online erfassen und Anbieter finden.",
    h1: "Homelift Anbieter in der Schweiz finden",
    intro:
      "Ein Homelift erschliesst ein privates Wohnhaus über mehrere Geschosse, mit deutlich weniger Platzbedarf als ein klassischer Personenaufzug.",
    cta: "Homelift-Anfrage erfassen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Was ist ein Homelift?",
        answer:
          "Ein Homelift ist ein kompakter Aufzug für private Wohnhäuser. Er kommt mit kleiner Grube und geringer Schachtkopfhöhe aus, fährt langsamer als ein Personenaufzug und ist für wenige Nutzerinnen und Nutzer ausgelegt.",
      },
      {
        h2: "Wann ein Homelift passt",
        bullets: [
          "Einfamilienhaus oder Stadtvilla über zwei bis vier Geschosse",
          "altersgerechtes Wohnen im eigenen Haus",
          "Rollstuhl- oder Rollatornutzung im Alltag",
          "Nachrüstung ohne grossen Eingriff in den Grundriss",
        ],
      },
      {
        h2: "Platzbedarf und Einbau",
        paragraphs: [
          "Homelifte werden innen in einem eigenen Schacht, im Treppenauge oder als Anbau an die Fassade geführt. Entscheidend sind die Grundfläche, die Deckendurchbrüche und die Geschosshöhen. Ein Anbau ist im bewohnten Zustand oft der ruhigere Weg, braucht aber eine gestalterische und baurechtliche Abklärung.",
        ],
      },
      {
        h2: "Homelift oder Treppenlift",
        paragraphs: [
          "Ein Treppenlift ist günstiger und schneller montiert, belegt aber die Treppe und ist auf sie beschränkt. Ein Homelift bietet eine eigene Kabine, mehr Komfort und Transportmöglichkeiten, verlangt jedoch Platz und Bauarbeiten.",
        ],
      },
      {
        h2: "Kostenfaktoren",
        bullets: [
          "Anzahl Haltestellen",
          "Schacht innen oder Anbau aussen",
          "notwendige Deckendurchbrüche und Verstärkungen",
          "Kabinenmass und Ausstattung",
          "Anforderungen an Barrierefreiheit",
          "Wartung im Betrieb",
        ],
      },
      {
        h2: "Ablauf",
        paragraphs: [
          "Erfassen Sie Geschosse, geplanten Standort und Nutzung. Passende Anbieter melden sich direkt bei Ihnen und prüfen die Machbarkeit vor Ort, bevor eine Variante empfohlen wird.",
        ],
      },
    ],
    faq: [
      {
        q: "Braucht ein Homelift einen Maschinenraum?",
        a: "Viele Homelifte kommen ohne separaten Maschinenraum aus. Ob das für Ihre Situation gilt, beurteilt der Anbieter anhand von Schacht, Antrieb und Fahrhöhe.",
      },
      {
        q: "Ist ein Homelift rollstuhlgängig?",
        a: "Das hängt vom Kabinenmass und der Türbreite ab. Geben Sie in der Anfrage an, wenn eine rollstuhlgängige Lösung nötig ist.",
      },
    ],
    related: [
      { to: "/lift-einfamilienhaus-schweiz", label: "Lift im Einfamilienhaus" },
      { to: "/treppenlift-schweiz", label: "Treppenlift Anbieter finden" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
      { to: "/lift-nachtraeglich-einbauen", label: "Lift nachträglich einbauen" },
    ],
  },

  {
    slug: "/lift-nachtraeglich-einbauen",
    title: "Lift nachträglich einbauen Schweiz | Anbieter finden",
    description:
      "Lift nachträglich einbauen in der Schweiz: Varianten im Bestand, Machbarkeit, Bewilligungen und Ablauf. Projekt online erfassen und Anbieter finden.",
    h1: "Lift nachträglich einbauen in der Schweiz",
    intro:
      "In vielen Bestandsbauten lässt sich ein Lift nachrüsten. Welche Variante möglich ist, entscheidet sich am Gebäude, nicht am Katalog.",
    cta: "Liftprojekt erfassen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Ist ein nachträglicher Lifteinbau möglich?",
        answer:
          "In den meisten Fällen ja, sofern Platz für Schacht und Haltestellen gefunden wird. Üblich sind ein Einbau ins Treppenauge, ein neuer Schacht in Nebenräumen oder ein Anbau an die Fassade. Die Machbarkeit wird vor Ort beurteilt.",
      },
      {
        h2: "Varianten im Bestand",
        bullets: [
          "Treppenauge: nutzt vorhandenen Raum, verlangt genügend Querschnitt und Anpassungen am Geländer.",
          "Nebenräume: Estrich, Abstellraum oder Reduit werden zum Schacht, dafür entfällt Nutzfläche.",
          "Anbau aussen: geringer Eingriff im Inneren, dafür Fassade, Bewilligung und Gestaltung zu klären.",
        ],
      },
      {
        h2: "Was die Machbarkeit bestimmt",
        bullets: [
          "Statik von Decken und Wänden",
          "Brandschutz und Fluchtwege",
          "Geschosshöhen, Grube und Schachtkopf",
          "Erschliessung aller gewünschten Geschosse",
          "Nutzung des Gebäudes während der Bauzeit",
        ],
      },
      {
        h2: "Bewilligungen",
        paragraphs: [
          "Eingriffe in Tragstruktur, Fassade oder Fluchtwege sind in der Regel bewilligungspflichtig. Bei Stockwerkeigentum kommt ein Beschluss der Gemeinschaft dazu. Die Anbieter kennen die Anforderungen in Ihrer Region und sagen, welche Unterlagen nötig sind.",
        ],
      },
      {
        h2: "Ablauf",
        paragraphs: [
          "Erfassen Sie Gebäude, Geschosse und die angedachte Variante. Registrierte Liftanbieter melden sich direkt bei Ihnen, prüfen die Situation vor Ort und zeigen mögliche Wege auf.",
        ],
      },
    ],
    faq: [
      {
        q: "Wie lange dauert eine Nachrüstung?",
        a: "Das hängt von Variante und Bewilligung ab. Ein Anbau lässt sich häufig etappiert bauen, ein neuer Schacht im Inneren greift stärker in die Nutzung ein. Termine nennt der Anbieter nach der Abklärung.",
      },
      {
        q: "Muss ich während der Bauzeit ausziehen?",
        a: "In der Regel nicht. Wie die Erschliessung während der Arbeiten aussieht, planen Sie mit dem Anbieter.",
      },
    ],
    related: [
      { to: "/liftbau-schweiz", label: "Liftbau in der Schweiz" },
      { to: "/lift-einfamilienhaus-schweiz", label: "Lift im Einfamilienhaus" },
      { to: "/homelift-schweiz", label: "Homelift für Wohnhäuser" },
      { to: "/plattformlift-schweiz", label: "Plattformlift und Rollstuhllift" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
    ],
  },

  {
    slug: "/lift-abo-schweiz",
    parent: { to: "/liftservice-schweiz", label: "Liftservice" },
    title: "Lift-Abo Schweiz: Wartungsvertrag & Anbieter | Liftblick",
    description:
      "Lift-Abo in der Schweiz: was ein Wartungsvertrag üblicherweise enthält, wie Laufzeit und Kündigung geregelt sind und wann sich ein Anbieterwechsel lohnt.",
    h1: "Lift-Abo in der Schweiz",
    intro:
      "Ein Lift-Abo ist in der Regel ein Wartungsvertrag für eine bestehende Liftanlage. Welche Leistungen darin enthalten sind, ist nicht einheitlich geregelt und unterscheidet sich von Anbieter zu Anbieter. Liftblick.ch wartet keine Anlagen, sondern bringt Ihre Wartungsanfrage zu registrierten Liftfirmen.",
    cta: "Wartungsanfrage kostenlos erfassen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Was ist ein Lift-Abo?",
        answer:
          "Als Lift-Abo wird meist ein Wartungs- oder Servicevertrag für einen Aufzug bezeichnet: Die Liftfirma übernimmt gegen eine periodische Pauschale die regelmässige Wartung und in der Regel den Störungsdienst. Der genaue Umfang ergibt sich ausschliesslich aus dem Vertrag.",
        paragraphs: [
          "Der Begriff ist kein geschützter Standard. Zwei Angebote mit gleichem Namen können sehr unterschiedliche Leistungen enthalten, etwa bei Reparaturen, Ersatzteilen oder der Erreichbarkeit ausserhalb der Bürozeiten. Ein Vergleich lohnt sich deshalb auf Ebene der Leistungen, nicht auf Ebene der Bezeichnung.",
        ],
      },
      {
        h2: "Was ist in einem Lift-Abo enthalten?",
        bullets: [
          "periodische Wartung der Anlage",
          "Kontrolle von Antrieb, Türen, Steuerung und Sicherheitseinrichtungen",
          "Störungsdienst und Personenbefreiung, je nach Vertrag mit definierter Erreichbarkeit",
          "Rapporte und Dokumentation der Arbeiten",
          "Begleitung wiederkehrender Kontrollen, sofern vereinbart",
          "Reparaturen und Ersatzteile: je nach Vertrag inbegriffen, teilweise inbegriffen oder ausgenommen",
        ],
      },
      {
        h2: "Wartung und regelmässige Kontrollen",
        paragraphs: [
          "Wie oft gewartet wird, richtet sich nach Anlagentyp, Alter und Nutzungsintensität. Ein Lift in einem Bürogebäude mit hoher Frequenz wird anders betreut als eine Anlage in einem kleinen Mehrfamilienhaus. Zur Wartung gehören Sichtprüfung, Funktionskontrolle, Einstellarbeiten und die Beurteilung von Verschleissteilen.",
          "Wiederkehrende Kontrollen durch unabhängige Stellen sind je nach Kanton und Anlagentyp zusätzlich vorgesehen. Klären Sie im Vertrag, ob die Begleitung dieser Kontrollen enthalten ist.",
        ],
      },
      {
        h2: "Störungsdienst",
        paragraphs: [
          "Der Störungsdienst regelt, wer erreichbar ist, wenn die Anlage stillsteht oder Personen eingeschlossen sind. Wichtig sind die Erreichbarkeit rund um die Uhr, die vereinbarten Reaktionszeiten und die Frage, ob Einsätze bei Störung im Abo enthalten sind oder separat verrechnet werden.",
          "Bei einem akuten Stillstand mit eingeschlossenen Personen rufen Sie immer direkt die Notrufnummer Ihrer Wartungsfirma an. Ein Onlineformular ist dafür nicht der richtige Weg.",
        ],
        link: {
          text: "Wartung, Servicefirma und Anbieterwechsel im Überblick:",
          to: "/liftservice-schweiz",
          label: "Liftservice Schweiz",
        },
      },
      {
        h2: "Reparaturen und Ersatzteile",
        paragraphs: [
          "Hier liegt der grösste Unterschied zwischen Angeboten. Manche Verträge decken nur Wartung, andere schliessen Verschleissteile ein, und Vollverträge übernehmen zusätzlich definierte Reparaturen. Prüfen Sie, welche Bauteile ausgenommen sind, wie Preisanpassungen geregelt sind und wie lange Ersatzteile für Ihre Anlage verfügbar bleiben.",
        ],
      },
      {
        h2: "Laufzeit und Kündigungsbedingungen",
        bullets: [
          "Vertragsdauer und automatische Verlängerung",
          "Kündigungsfrist und massgebender Termin",
          "Regelung bei Eigentümerwechsel oder Verkauf der Liegenschaft",
          "Bedingungen bei Ersatz oder Modernisierung der Anlage",
          "Anpassung der Pauschale während der Laufzeit",
        ],
      },
      {
        h2: "Herstellerabhängige vs. unabhängige Wartung",
        paragraphs: [
          "Die Wartung durch den Hersteller der Anlage bietet direkten Zugang zu Steuerung, Software und Originalteilen. Unabhängige Servicefirmen betreuen gemischte Bestände und sind oft flexibler in der Vertragsgestaltung. Ob ein Wechsel technisch möglich ist, hängt vor allem vom Zugang zur Steuerung und von der Ersatzteilsituation ab. Das beurteilt die Firma an Ihrer Anlage.",
        ],
      },
      {
        h2: "Wann lohnt sich ein Anbieterwechsel?",
        bullets: [
          "Störungen treten häufiger auf als früher",
          "vereinbarte Reaktionszeiten werden nicht eingehalten",
          "Rapporte und Abrechnungen sind nicht nachvollziehbar",
          "der Vertrag läuft seit Jahren unverändert weiter",
          "die Anlage wurde umgebaut, der Vertrag aber nicht angepasst",
          "mehrere Liegenschaften sollen einheitlich betreut werden",
        ],
      },
      {
        h2: "Welche Angaben benötigt ein neuer Wartungsanbieter?",
        bullets: [
          "Standort und Anzahl Anlagen",
          "Liftart, Tragkraft und Anzahl Haltestellen",
          "Hersteller und Baujahr, wenn bekannt",
          "aktuelle Wartungsfirma sowie Laufzeit und Kündigungsfrist des Vertrags",
          "Störungshäufigkeit der letzten Monate",
          "gewünschter Leistungsumfang, etwa mit oder ohne Reparaturen",
        ],
      },
      {
        h2: "So funktioniert eine Wartungsanfrage über Liftblick",
        paragraphs: [
          "Sie erfassen Ihre Anlage und den gewünschten Leistungsumfang online. Liftblick.ch ist die Plattform: Registrierte Liftanbieter sehen passende Anfragen im geschützten Portal. Interessierte Anbieter melden sich direkt bei Ihnen. Technische Bedingungen und Preise vereinbaren Sie ausschliesslich direkt mit dem Anbieter.",
        ],
      },
    ],
    faq: [
      {
        q: "Ist ein Lift-Abo dasselbe wie ein Wartungsvertrag?",
        a: "In der Praxis wird beides gleichbedeutend verwendet. Massgebend ist nicht die Bezeichnung, sondern der im Vertrag festgehaltene Leistungsumfang.",
      },
      {
        q: "Ist ein Lift-Abo gesetzlich vorgeschrieben?",
        a: "Vorgeschrieben ist der sichere Betrieb der Anlage, nicht ein bestimmter Vertrag. In der Praxis wird die regelmässige Wartung über einen Wartungsvertrag mit einer Liftfirma sichergestellt.",
      },
      {
        q: "Kann eine andere Firma meine Anlage warten?",
        a: "In vielen Fällen ja, auch bei einer Anlage eines anderen Herstellers. Massgebend sind der Zugang zur Steuerung und die Verfügbarkeit von Ersatzteilen. Das beurteilt die Firma an der Anlage.",
      },
      {
        q: "Wie finde ich einen neuen Liftservice-Anbieter?",
        a: "Sie erfassen Ihre Anlage und den gewünschten Leistungsumfang online. Registrierte Liftfirmen sehen passende Anfragen im geschützten Portal und melden sich direkt bei Ihnen.",
      },
      {
        q: "Wartet Liftblick.ch selbst Lifte?",
        a: "Nein. Liftblick.ch wartet keine Anlagen und erstellt keine Angebote. Die Wartung übernimmt die von Ihnen gewählte Liftfirma.",
      },
      {
        q: "Nennt Liftblick.ch Preise für ein Lift-Abo?",
        a: "Nein. Die Pauschale hängt von Anlage, Leistungsumfang und Standort ab und wird direkt zwischen Ihnen und dem Anbieter vereinbart.",
      },
    ],
    related: [
      { to: "/liftservice-schweiz", label: "Liftservice Schweiz" },
      { to: "/liftreparatur-schweiz", label: "Lift reparieren lassen" },
      { to: "/liftmodernisierung-schweiz", label: "Liftmodernisierung" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen" },
      { to: "/liftanbieter-schweiz", label: "Liftanbieter finden" },
    ],

  },

  {
    slug: "/liftreparatur-schweiz",
    parent: { to: "/liftservice-schweiz", label: "Liftservice" },
    title: "Lift defekt oder kaputt? Reparatur & Störungen | Liftblick",
    description:
      "Lift defekt, kaputt oder ausser Betrieb? Erfahren Sie, wie Sie eine Liftstörung melden und wie Sie eine Reparatur bei einer Liftfirma in der Schweiz anfragen.",
    h1: "Lift defekt oder kaputt? Das können Sie jetzt tun.",
    intro:
      "Ihr Lift funktioniert nicht mehr, bleibt stehen oder die Türen öffnen nicht? Erfahren Sie, an wen Sie eine Liftstörung melden sollten und wie Sie eine Reparatur organisieren. Für nicht akute Reparaturanfragen können Sie Ihr Anliegen kostenlos über Liftblick erfassen. Registrierte Liftfirmen können Ihre Anfrage einsehen und Sie direkt kontaktieren.",
    cta: "Reparaturanfrage erfassen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Personen im Lift eingeschlossen? Sofort Notruf nutzen",
        answer:
          "Sind Personen im Lift eingeschlossen, drücken Sie den Alarmknopf in der Kabine oder rufen Sie die Notrufnummer der zuständigen Wartungsfirma an. Liftblick ist kein Notfalldienst: Wir nehmen keine Störungsmeldungen entgegen, schicken keine Techniker und haben keine 24-Stunden-Hotline.",
        paragraphs: [
          "Die Notrufnummer der Wartungsfirma steht meist in der Kabine, beim Lift im Erdgeschoss oder bei der Hausverwaltung. Versuchen Sie nicht, Türen selbst zu öffnen oder Personen eigenhändig zu befreien.",
        ],
      },
      {
        h2: "Lift defekt: Wen muss ich informieren?",
        bullets: [
          "Mieterinnen und Mieter: Hausverwaltung, Hauswart oder den Notfallkontakt der Wartungsfirma",
          "Eigentümerinnen und Eigentümer: die zuständige Wartungsfirma oder den Liftservice-Anbieter",
          "Akuter Notfall mit eingeschlossenen Personen: sofort den vorgesehenen Notruf",
        ],
      },
      {
        h2: "Liftstörung melden: So gehen Sie vor",
        paragraphs: [
          "Wer eine Liftstörung meldet, hilft der Liftfirma mit wenigen klaren Angaben. Beschreiben Sie, was Sie beobachten, ohne selbst nach der Ursache zu suchen.",
        ],
        bullets: [
          "Adresse des Gebäudes",
          "Standort des Lifts, etwa Treppenhaus A oder Hauptlift",
          "Beschreibung des Problems",
          "ob der Lift noch fährt oder ganz stillsteht",
          "seit wann die Störung besteht",
          "Fehlercode auf der Anzeige, falls sichtbar",
          "bestehende Wartungsfirma, falls bekannt",
        ],
      },
      {
        h2: "Lift kaputt: Wann braucht es eine Reparatur?",
        paragraphs: [
          "Nicht jede Störung bedeutet einen grossen Schaden. Eine vorübergehende Störung kann nach einem Neustart durch die Fachfirma behoben sein. Tritt dasselbe Problem immer wieder auf, steckt oft ein technisches Problem dahinter, das genauer untersucht werden muss.",
          "Beschädigte Bauteile wie Türen, Seile oder Teile der Steuerung müssen ersetzt oder instand gestellt werden. Fällt der Lift ganz aus, ist er ausser Betrieb, bis eine Fachperson ihn geprüft hat. Welcher Fall vorliegt, kann nur die Liftfirma vor Ort beurteilen, nicht eine Ferndiagnose.",
        ],
      },
      {
        h2: "Lift fährt nicht mehr oder bleibt stehen",
        paragraphs: [
          "Fährt der Lift nicht mehr oder bleibt er zwischen zwei Stockwerken stehen, benutzen Sie ihn nicht weiter und informieren Sie andere Bewohnerinnen und Bewohner. Melden Sie die Störung der Hausverwaltung oder der Wartungsfirma. Öffnen Sie keine Schachttüren, schalten Sie nichts am Lift um und versuchen Sie keine eigenen Reparaturen. Sind Personen in der Kabine, gilt immer zuerst der Notruf.",
        ],
      },
      {
        h2: "Aufzug ausser Betrieb im Mehrfamilienhaus",
        paragraphs: [
          "Im Mietshaus ist in der Regel die Eigentümerschaft für den Lift zuständig, die Organisation übernimmt meist die Hausverwaltung. Mieterinnen und Mieter melden einen defekten Lift deshalb der Verwaltung oder dem Hauswart. Bei Stockwerkeigentum kümmert sich die Verwaltung der Gemeinschaft um die Beauftragung der Wartungsfirma.",
          "Eigentümer und Verwaltungen, die keine Wartungsfirma haben oder eine zweite Einschätzung möchten, können eine Reparaturanfrage erfassen.",
        ],
      },
      {
        h2: "Liftreparatur in der Schweiz anfragen",
        answer:
          "Sie beschreiben eine nicht akute Reparatur online. Registrierte Liftfirmen können die Anfrage einsehen, interessierte Firmen kontaktieren Sie direkt. Termin, Verfügbarkeit und Preis vereinbaren Sie mit der Firma.",
        paragraphs: [
          "Liftblick führt keine Reparaturen aus und garantiert keine Reaktionszeiten. Die Plattform bringt Ihre Anfrage zu Liftfirmen, die sich bei Interesse selbst bei Ihnen melden.",
        ],
      },
      {
        h2: "Liftservice oder Liftreparatur: Was ist der Unterschied?",
        paragraphs: [
          "Eine Liftreparatur behebt eine konkrete Störung oder einen Schaden. Liftservice umfasst die laufende Wartung und den Servicevertrag, der den Lift dauerhaft betriebsbereit halten soll.",
        ],
        link: {
          text: "Suchen Sie eine Liftfirma für regelmässige Wartung oder einen neuen Servicevertrag?",
          to: "/liftservice-schweiz",
          label: "Mehr zum Liftservice",
        },
      },
    ],
    faq: [
      {
        q: "Was tun, wenn der Lift kaputt ist?",
        a: "Benutzen Sie den Lift nicht weiter und melden Sie die Störung der Hausverwaltung oder der Wartungsfirma. Sind Personen eingeschlossen, nutzen Sie sofort den Alarmknopf oder die Notrufnummer der Wartungsfirma.",
      },
      {
        q: "Wen muss ich informieren, wenn der Lift defekt ist?",
        a: "Als Mieterin oder Mieter die Hausverwaltung oder den Hauswart. Als Eigentümerin oder Eigentümer die zuständige Wartungsfirma.",
      },
      {
        q: "Wie kann ich eine Liftstörung melden?",
        a: "Melden Sie die Störung bei der Wartungsfirma oder Verwaltung mit Adresse, Standort des Lifts, Beschreibung, Zeitpunkt und einem sichtbaren Fehlercode. Liftblick nimmt keine Störungsmeldungen entgegen.",
      },
      {
        q: "Was tun, wenn der Lift nicht mehr fährt?",
        a: "Nicht weiter benutzen, keine Türen oder Schalter selbst bedienen und die Störung melden. Eine Fachperson muss den Lift prüfen, bevor er wieder in Betrieb geht.",
      },
      {
        q: "Wer ist für einen defekten Lift im Mietshaus zuständig?",
        a: "In der Regel die Eigentümerschaft, vertreten durch die Hausverwaltung. Sie beauftragt die Wartungsfirma mit der Reparatur.",
      },
      {
        q: "Kann eine andere Liftfirma meinen Lift reparieren?",
        a: "Häufig ja. Massgebend sind der Zugang zur Steuerung, die Verfügbarkeit von Ersatzteilen und die Regelungen im bestehenden Wartungsvertrag.",
      },
      {
        q: "Wie finde ich eine Liftfirma für eine Reparatur?",
        a: "Für nicht akute Reparaturen erfassen Sie Ihr Anliegen kostenlos über Liftblick. Registrierte Liftfirmen können die Anfrage einsehen und Sie direkt kontaktieren.",
      },
      {
        q: "Kann Liftblick sofort einen Techniker schicken?",
        a: "Nein. Liftblick ist kein Notfall- oder Reparaturdienst, schickt keine Techniker und garantiert keine Reaktionszeiten. Im Notfall gilt immer der Notruf der Wartungsfirma.",
      },
    ],
    related: [
      { to: "/liftservice-schweiz", label: "Liftservice Schweiz" },
      { to: "/lift-abo-schweiz", label: "Lift-Abo und Wartungsvertrag" },
      { to: "/liftmodernisierung-schweiz", label: "Liftmodernisierung" },
      { to: "/liftanbieter-schweiz", label: "Liftanbieter Schweiz" },
    ],
  },

  {
    slug: "/lift-einfamilienhaus-schweiz",
    parent: { to: "/liftarten", label: "Liftarten" },
    title: "Lift im Einfamilienhaus Schweiz: Planung & Anbieter | Liftblick",
    description:
      "Lift im Einfamilienhaus: Homelift oder Personenaufzug, Platzbedarf, Neubau oder Nachrüstung, Barrierefreiheit und welche Angaben Anbieter benötigen.",
    h1: "Lift im Einfamilienhaus",
    intro:
      "Ein Lift im eigenen Haus macht alle Geschosse langfristig nutzbar, vom Keller bis zum Dachgeschoss. Welche Lösung passt, entscheidet sich am Haus: Platz, Geschosszahl und die Frage, ob innen oder aussen gebaut wird.",
    cta: "Liftprojekt kostenlos erfassen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Wann ist ein Lift im Einfamilienhaus sinnvoll?",
        answer:
          "Meist dann, wenn das Haus langfristig bewohnt bleiben soll: bei altersgerechtem Umbau, bei eingeschränkter Mobilität, bei mehr als zwei Wohngeschossen oder wenn ein Umbau ohnehin ansteht und der Lift gleich mitgeplant werden kann.",
        paragraphs: [
          "Auch der Alltag spielt mit: Einkäufe, Wäsche und Kinderwagen zwischen Keller und Wohngeschoss sind ein häufiger Grund. Wer den Lift bereits bei einer Sanierung mitdenkt, spart spätere Eingriffe in Decken und Grundriss.",
        ],
      },
      {
        h2: "Homelift oder Personenaufzug?",
        bullets: [
          "Homelift: kompakt, langsamere Fahrt, kleine Grube und geringe Schachtkopfhöhe, ausgelegt auf wenige Personen.",
          "Personenaufzug: höhere Tragkraft und Geschwindigkeit, mehr Platzbedarf, sinnvoll bei intensiver Nutzung oder grosszügigen Kabinenmassen.",
          "Treppenlift oder Plattformlift: Alternative, wenn kein Platz für einen Schacht vorhanden ist.",
        ],
      },
      {
        h2: "Neubau oder nachträglicher Einbau?",
        paragraphs: [
          "Im Neubau wird der Schacht Teil der Tragstruktur, was Grundriss und Fundament früh festlegt. Im Bestand geht es um Machbarkeit: Deckendurchbrüche, Statik, verfügbare Nebenräume und die Frage, wie das Haus während der Bauzeit bewohnbar bleibt. Beide Wege sind üblich, der Aufwand unterscheidet sich deutlich.",
        ],
      },
      {
        h2: "Platzbedarf",
        paragraphs: [
          "Entscheidend sind die verfügbare Grundfläche, die Geschosshöhen, die nötige Grube und die Höhe über der obersten Haltestelle. Kompakte Anlagen brauchen weniger davon, ganz ohne bauliche Vorbereitung geht es aber nicht. Fotos und ein Grundriss helfen den Anbietern mehr als Beschreibungen.",
        ],
      },
      {
        h2: "Anzahl Stockwerke und Haltestellen",
        paragraphs: [
          "Notieren Sie, welche Geschosse erschlossen werden sollen, inklusive Keller, Garage und Dachgeschoss. Jede zusätzliche Haltestelle beeinflusst Schachtlänge, Türen und Aufwand. Häufig genügen zwei oder drei Haltestellen für den Alltag.",
        ],
      },
      {
        h2: "Innen- oder Aussenlift",
        bullets: [
          "Innen im eigenen Schacht: geschützte Lage, dafür Eingriff in Decken und Nutzfläche.",
          "Innen im Treppenauge: nutzt vorhandenen Raum, verlangt genügend Querschnitt und Anpassungen am Geländer.",
          "Aussen als Anbau an die Fassade: im bewohnten Zustand oft ruhiger zu bauen, dafür Gestaltung, Wetterschutz und Baubewilligung zu klären.",
        ],
      },
      {
        h2: "Bestehender Treppenraum oder separater Schacht",
        paragraphs: [
          "Ein Einbau im Treppenauge spart Fläche, verändert aber die Treppe und braucht ausreichend Querschnitt. Ein separater Schacht in einem Abstellraum oder Reduit lässt die Treppe unberührt, kostet dafür Nutzfläche. Welche Variante möglich ist, zeigt die Beurteilung vor Ort.",
        ],
      },
      {
        h2: "Barrierefreiheit",
        paragraphs: [
          "Soll der Lift rollstuhlgängig sein, gelten Mindestmasse für Kabine, Türbreite und Bewegungsfläche davor. Denken Sie den ganzen Weg mit: Vorplatz, Hauseingang, Schwellen und Türbreiten im Haus entscheiden mit darüber, ob die Lösung im Alltag wirklich funktioniert.",
        ],
      },
      {
        h2: "Mögliche bauliche Anforderungen",
        bullets: [
          "Statik von Decken und Wänden, teilweise Verstärkungen",
          "Grube und Höhe über der obersten Haltestelle",
          "Elektroanschluss und Absicherung",
          "Brandschutz und Fluchtwege",
          "Baubewilligung bei Eingriffen in Tragstruktur oder Fassade",
          "Wiederherstellungsarbeiten an Boden, Wänden und Treppe",
        ],
      },
      {
        h2: "Welche Angaben Anbieter benötigen",
        bullets: [
          "Standort und Postleitzahl",
          "Anzahl Geschosse und gewünschte Haltestellen",
          "geplanter Standort des Lifts, innen oder aussen",
          "Nutzung: Personen, Rollstuhl, Rollator, Transporte",
          "Neubau, Umbau oder Nachrüstung im bewohnten Zustand",
          "Fotos, Grundrisse oder Pläne, wenn vorhanden",
          "gewünschter Zeitraum",
        ],
      },
      {
        h2: "Welche Faktoren die Kosten beeinflussen",
        paragraphs: [
          "Der Preis entsteht aus der Anlage, den Bauarbeiten am Haus und den laufenden Kosten für Wartung. Wie stark die Bauarbeiten ins Gewicht fallen, hängt von Deckendurchbrüchen, Verstärkungen und Wiederherstellung ab. Liftblick.ch nennt keine Pauschalpreise. Welche Faktoren den Preis bestimmen, steht auf der Seite zu den Aufzugskosten, verbindliche Zahlen nennt der Anbieter nach der Abklärung vor Ort.",
        ],
      },
    ],
    faq: [
      {
        q: "Passt ein Lift auch in ein bestehendes Einfamilienhaus?",
        a: "In vielen Fällen ja. Üblich sind ein Einbau im Treppenauge, ein neuer Schacht in einem Nebenraum oder ein Anbau an die Fassade. Die Machbarkeit wird vor Ort beurteilt.",
      },
      {
        q: "Wie viele Haltestellen brauche ich?",
        a: "Das richtet sich nach dem Alltag. Häufig sind es Keller oder Garage, Erdgeschoss und Obergeschoss. Jede zusätzliche Haltestelle erhöht den Aufwand.",
      },
      {
        q: "Was kostet ein Lift im Einfamilienhaus?",
        a: "Dafür gibt es keinen Listenpreis, weil der bauliche Anteil von Haus zu Haus stark schwankt. Die Kostenfaktoren finden Sie auf der Seite zu den Aufzugskosten, eine Zahl nennt Ihnen der Anbieter nach der Beurteilung Ihrer Situation.",
      },
      {
        q: "Ist ein Aussenlift beim Einfamilienhaus möglich?",
        a: "Häufig ja, als Anbau an die Fassade. Zu klären sind Gestaltung, Wetterschutz, Anschluss an die Geschosse und die Baubewilligung.",
      },
      {
        q: "Homelift oder Treppenlift?",
        a: "Ein Treppenlift ist günstiger und schneller montiert, bleibt aber auf die Treppe beschränkt. Ein Homelift bietet eine eigene Kabine und mehr Komfort, verlangt jedoch Platz und Bauarbeiten.",
      },
      {
        q: "Wie finde ich passende Liftanbieter in der Schweiz?",
        a: "Sie erfassen Ihr Projekt online. Registrierte Liftanbieter sehen passende Anfragen im geschützten Portal und melden sich direkt bei Ihnen.",
      },
    ],
    related: [
      { to: "/homelift-schweiz", label: "Homelift Schweiz" },
      { to: "/lift-nachtraeglich-einbauen", label: "Lift nachträglich einbauen" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzug Kosten Schweiz" },
      { to: "/lift-planen", label: "Lift planen" },
      { to: "/treppenlift-schweiz", label: "Treppenlift Anbieter finden" },
      { to: "/liftanbieter-schweiz", label: "Liftanbieter finden" },
    ],

  },

  {
    slug: "/personenaufzug-schweiz",
    parent: { to: "/liftarten", label: "Liftarten" },
    title: "Personenaufzug Schweiz: Kosten & Anbieter | Liftblick",
    description:
      "Personenaufzug planen: Kostenfaktoren, Kabine für 2 oder 4 Personen und Einbau klären. Projekt kostenlos erfassen und Schweizer Liftanbieter finden.",
    h1: "Personenaufzug in der Schweiz",
    intro:
      "Sie planen einen Personenaufzug für ein Wohn- oder Geschäftsgebäude? Entscheidend sind Nutzung, Haltestellen, Kabinengrösse und der verfügbare Schacht. Hier finden Sie die wichtigsten Kostenfaktoren und Planungsfragen, auch für einen Aufzug für 2 oder 4 Personen. Über Liftblick erfassen Sie Ihr Projekt kostenlos und unverbindlich. Nach der Prüfung Ihrer Anfrage können registrierte Schweizer Liftanbieter direkt Kontakt aufnehmen. Liftblick ist eine Anfrageplattform und baut oder verkauft selbst keine Aufzüge.",
    cta: "Personenaufzug anfragen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Was ist ein Personenaufzug und wo wird er eingesetzt?",
        answer:
          "Ein Personenaufzug befördert Menschen in einer Kabine zwischen den Haltestellen eines Gebäudes. Er wird in Wohnhäusern, Geschäftsliegenschaften und öffentlichen Bauten eingesetzt. Welche Ausführung passt, hängt von der Nutzung, den Platzverhältnissen und den Anforderungen an die Zugänglichkeit ab.",
        bullets: [
          "Einfamilienhäuser: wenige Haltestellen, kompakte Anlage, Komfort und Altersvorsorge.",
          "Mehrfamilienhäuser: Erschliessung aller Wohngeschosse plus Keller und Waschraum, ruhiger Betrieb.",
          "Geschäftsliegenschaften: höhere Frequenz, kurze Wartezeiten, Verfügbarkeit im Betrieb.",
          "öffentliche Gebäude: Barrierefreiheit, Orientierung und robuste Ausführung im Vordergrund.",
        ],
      },
      {
        h2: "Aufzug für 2 oder 4 Personen: Welche Kabine passt?",
        answer:
          "Die gewünschte Personenzahl ist ein Ausgangspunkt für die Planung, legt aber keine einheitlichen Kabinen- oder Schachtmasse fest. Für einen Aufzug für 2 oder 4 Personen müssen Anbieter Tragfähigkeit, nutzbare Kabinenfläche, Türbreite und vorhandenen Platz gemeinsam prüfen. Auch eine Begleitperson, ein Kinderwagen oder die Rollstuhlnutzung können die Auswahl verändern.",
        paragraphs: [
          "Beschreiben Sie, wer den Lift im Alltag nutzt und ob Personen gleichzeitig fahren sollen. Eine kleine Kabine für gelegentliche private Fahrten stellt andere Anforderungen als ein Personenlift in einem Mehrfamilienhaus mit regelmässiger Nutzung.",
          "Die Angabe «4 Personen» bestätigt für sich allein keine Rollstuhlgängigkeit. Klären Sie den Zugang zur Kabine, die Türöffnung und die Bewegungsflächen separat. Verbindliche Masse nennt die Liftfirma für die konkret vorgeschlagene Anlage, nicht eine allgemeine Tabelle nach Personenzahl.",
        ],
        link: { text: "Für einen privaten Wohnbau lohnt sich auch der Vergleich:", to: "/homelift-schweiz", label: "Homelift in der Schweiz" },
      },
      {
        h2: "Was kostet ein Personenaufzug in der Schweiz?",
        answer:
          "Die Kosten eines Personenaufzugs hängen von der Anlage und den Arbeiten am Gebäude ab. Haltestellen, Kabine, Türen, Schacht und Montagebedingungen beeinflussen den Umfang. Ohne diese Angaben lässt sich kein belastbarer Gesamtpreis nennen. Liftblick veröffentlicht deshalb keine pauschalen CHF-Beträge und berechnet keine Aufzugspreise.",
        bullets: [
          "Anlage: Tragfähigkeit, Kabinengrösse, Türen und Ausstattung",
          "Erschliessung: Anzahl Haltestellen und Zugänge je Geschoss",
          "Bauarbeiten: vorhandener oder neuer Schacht, Durchbrüche und Wiederherstellung",
          "Einbau: Zugang zur Baustelle, Montage und Arbeiten im bewohnten Gebäude",
          "Bestehende Anlage: Demontage sowie Weiterverwendung geeigneter Bauteile",
          "Laufender Betrieb: Wartungsumfang und vereinbarte Serviceleistungen separat vergleichen",
        ],
        paragraphs: [
          "Vergleichen Sie Kostenauskünfte für denselben Projektumfang. Lassen Sie festhalten, ob Bauarbeiten, Elektroarbeiten, Demontage und Inbetriebnahme enthalten sind und welche Leistungen separat organisiert werden müssen.",
        ],
        link: { text: "Die einzelnen Kostenfaktoren erklärt der Ratgeber:", to: "/aufzug-kosten-schweiz", label: "Aufzug Kosten Schweiz" },
      },
      {
        h2: "Neubau oder Ersatzanlage",
        paragraphs: [
          "Im Neubau wird der Schacht zusammen mit der Tragstruktur geplant. Liftart, Kabinenmass, Türbreite und Tragfähigkeit sollten früh festgelegt sein, weil sie den Grundriss beeinflussen.",
          "Beim Ersatz einer bestehenden Anlage wird geprüft, ob der vorhandene Schacht weiterverwendet werden kann. Zu klären sind die nutzbaren Schachtmasse, die Dauer des Stillstands und wie das Gebäude in dieser Zeit erschlossen bleibt.",
        ],
      },
      {
        h2: "Anzahl Haltestellen",
        paragraphs: [
          "Halten Sie fest, welche Geschosse bedient werden sollen, inklusive Untergeschoss, Garage und Dachgeschoss, und ob Haltestellen auf gegenüberliegenden Seiten nötig sind. Die Anzahl Haltestellen bestimmt Schachtlänge, Anzahl Türen und Aufwand.",
        ],
      },
      {
        h2: "Tragfähigkeit und Kabinengrösse",
        paragraphs: [
          "Die erforderliche Tragfähigkeit und Kabinengrösse werden aus der vorgesehenen Nutzung abgeleitet. Nennen Sie der Liftfirma neben der Personenzahl auch Rollstuhl, Rollator, Kinderwagen oder geplante Transporte. Nutzbare Kabinenmasse, Türöffnung und Bewegungsflächen vor der Tür sind dabei getrennt zu prüfen.",
        ],
      },
      {
        h2: "Schacht",
        bullets: [
          "bestehender Schacht: nutzbare Masse und Eignung für die neue Anlage prüfen",
          "neuer Schacht innen: Eingriff in Decken und Grundriss, geschützte Lage der Anlage",
          "neuer Schacht aussen: Anbau an die Fassade und Anschlüsse an die Geschosse prüfen",
          "Grube, Höhe über der obersten Haltestelle und Elektroanschluss",
        ],
      },
      {
        h2: "Türen",
        paragraphs: [
          "Relevant sind die nutzbare Türbreite, die Öffnungsart, die Lage der Zugänge und die sichere Benutzung. Beschreiben Sie auch, ob Rollstühle, Kinderwagen oder grössere Gegenstände durch die Tür passen sollen. Die Liftfirma stimmt die Ausführung auf Kabine und Gebäudenutzung ab.",
        ],
      },
      {
        h2: "Barrierefreiheit",
        paragraphs: [
          "Soll der Personenaufzug rollstuhlgängig sein, muss die Planung mehr als die Kabine berücksichtigen: Türbreite, Bewegungsflächen, Bedienelemente und Übergänge gehören ebenfalls dazu. Prüfen Sie den ganzen Weg vom Gebäudeeingang bis zum Zielgeschoss. Welche Vorgaben im konkreten Bauprojekt gelten, klären die verantwortliche Planung und die Liftfirma.",
        ],
      },
      {
        h2: "Planung",
        bullets: [
          "Gebäudeart, Nutzung und erwartete Frequenz",
          "Anzahl Haltestellen und Geschosshöhen",
          "Schachtsituation und verfügbarer Platz",
          "Tragfähigkeit, Kabinenmass und Türbreite",
          "Anforderungen an Barrierefreiheit",
          "Termine, Etappen und tragbare Stillstandzeit",
        ],
      },
      {
        h2: "Modernisierung oder Ersatz",
        paragraphs: [
          "Bei einer bestehenden Anlage werden Zustand, Nutzung und verfügbare Ersatzteile beurteilt. Eine Modernisierung kann einzelne Komponenten wie Steuerung, Antrieb, Türen oder Kabine betreffen. Ein Ersatz wird ebenfalls geprüft, wenn die bestehende Lösung die Anforderungen nicht mehr erfüllt. Welche Variante technisch und wirtschaftlich sinnvoll ist, beurteilt die Liftfirma an der Anlage.",
        ],
        link: { text: "Mehr zur Abklärung einer bestehenden Anlage:", to: "/liftmodernisierung-schweiz", label: "Liftmodernisierung in der Schweiz" },
      },
    ],
    faq: [
      {
        q: "Was unterscheidet einen Personenaufzug von einem Homelift?",
        a: "Homelift bezeichnet meist eine Lösung für private Wohnhäuser. Personenaufzüge werden auch für Mehrfamilienhäuser, Geschäfts- und öffentliche Bauten geplant. Die Bezeichnung allein legt Geschwindigkeit, Tragfähigkeit oder Platzbedarf nicht fest. Vergleichen Sie die konkreten Anlagen und ihre Eignung für Ihre Nutzung.",
      },
      {
        q: "Welche Masse braucht ein Aufzug für 2 Personen?",
        a: "Es gibt keine allein aus der Personenzahl ableitbaren Kabinen- oder Schachtmasse. Die Liftfirma prüft die konkrete Anlage, Tragfähigkeit, Türöffnung und den verfügbaren Platz. Nennen Sie auch Anforderungen wie Rollator oder Begleitung.",
      },
      {
        q: "Ist ein Aufzug für 4 Personen automatisch rollstuhlgängig?",
        a: "Nein. Die Personenzahl allein bestätigt keine Rollstuhlgängigkeit. Kabine, Türbreite, Bewegungsflächen und Zugang müssen für die vorgesehene Rollstuhlnutzung separat geprüft werden.",
      },
      {
        q: "Welche Tragfähigkeit brauche ich?",
        a: "Das ergibt sich aus der Nutzung: Personenzahl, Rollstuhl oder Trage, Transporte. Beschreiben Sie den Alltag, die Anbieter leiten daraus Tragfähigkeit und Kabinenmass ab.",
      },
      {
        q: "Was kostet ein Personenaufzug mit Einbau?",
        a: "Der Gesamtpreis hängt von Anlage, Haltestellen, Schacht und Bauarbeiten ab. Klären Sie bei der Liftfirma, welche Leistungen enthalten sind. Liftblick nennt keine Pauschalpreise und führt keine automatische Preisberechnung durch.",
      },
      {
        q: "Kann ich einen Personenaufzug nachträglich einbauen?",
        a: "Das muss am Gebäude geprüft werden. Entscheidend sind der Platz für Kabine und Schacht, die Anschlüsse an die Geschosse und die nötigen Bauarbeiten. Grundrisse und Angaben zu den gewünschten Haltestellen helfen bei der ersten Abklärung.",
      },
      {
        q: "Wie finde ich Schweizer Anbieter für einen Personenlift?",
        a: "Erfassen Sie Ihr Projekt kostenlos und unverbindlich über Liftblick. Nach der Prüfung Ihrer Anfrage können registrierte Liftanbieter Kontakt mit Ihnen aufnehmen. Liftblick baut keine Aufzüge und erstellt selbst keine Offerten.",
      },
    ],
    related: [
      { to: "/liftbau-schweiz", label: "Liftbau Schweiz" },
      { to: "/lift-planen", label: "Lift planen" },
      { to: "/aufzug-kosten-schweiz", label: "Aufzug Kosten Schweiz" },
      { to: "/liftmodernisierung-schweiz", label: "Liftmodernisierung" },
      { to: "/liftanbieter-schweiz", label: "Liftanbieter Schweiz" },
    ],
  },

  {
    slug: "/plattformlift-schweiz",
    noindex: true,
    parent: { to: "/liftarten", label: "Liftarten" },
    title: "Plattformlift Schweiz: Anbieter & Lösungen | Liftblick",
    description:
      "Plattformlift und Rollstuhllift in der Schweiz: Treppen-Plattformlifte und vertikale Lösungen, Innen- und Aussenbereich, Platzbedarf und nötige Angaben.",
    h1: "Plattformlift in der Schweiz",
    intro:
      "Ein Plattformlift überwindet Höhenunterschiede mit einer befahrbaren Plattform und wird vor allem für die Rollstuhlnutzung eingesetzt. Erfassen Sie Ihre Situation online, registrierte Anbieter melden sich direkt bei Ihnen.",
    cta: "Plattformlift anfragen",
    bullets: TRUST_BULLETS,
    sections: [
      {
        h2: "Für wen eignet sich ein Plattformlift?",
        answer:
          "Ein Plattformlift eignet sich, wenn eine Treppe oder ein Höhenunterschied mit dem Rollstuhl oder mit einem Rollator überwunden werden muss und kein Platz für einen vollwertigen Aufzug vorhanden ist. Er wird sowohl privat als auch in Gewerbe- und öffentlichen Bauten eingesetzt.",
        bullets: [
          "Rollstuhlnutzung im Alltag, allein oder mit Begleitung",
          "private Gebäude: Zugang zur Haustür, Treppe im Innern, Weg vom Vorplatz",
          "Mehrfamilienhäuser: Eingangsbereich, Zugang zu Keller und Waschraum",
          "Gewerbe: Höhenunterschiede zwischen Verkaufs-, Büro- und Lagerflächen",
          "öffentliche Gebäude: barrierefreier Zugang zu Eingängen und Zwischengeschossen",
        ],
      },
      {
        h2: "Treppen-Plattformlift",
        paragraphs: [
          "Der Treppen-Plattformlift fährt entlang einer Schiene an der bestehenden Treppe. Die Plattform wird im Stillstand hochgeklappt, damit die Treppe begehbar bleibt. Entscheidend sind die Treppenbreite, der Verlauf der Treppe und der Platz an den Haltepunkten zum Auffahren und Wenden.",
        ],
      },
      {
        h2: "Vertikale Plattformlifte",
        paragraphs: [
          "Vertikale Plattformlifte, auch Hublifte genannt, fahren senkrecht und überwinden meist eine kleinere Höhe, etwa vom Vorplatz zur Haustür oder über ein Zwischengeschoss. Sie brauchen eine ebene Standfläche und je nach Höhe eine Umwehrung, dafür bleibt die Treppe vollständig frei.",
        ],
      },
      {
        h2: "Innenbereich und Aussenbereich",
        paragraphs: [
          "Im Aussenbereich sind wetterfeste Ausführung, geschützte Elektronik, Abdeckung im Stillstand und ein tragfähiger Untergrund massgebend. Im Innenbereich stehen Platzverhältnisse, Befestigung an Treppe oder Wand und die verbleibende Durchgangsbreite im Vordergrund.",
        ],
      },
      {
        h2: "Platzbedarf",
        bullets: [
          "Treppenbreite und verbleibende Durchgangsbreite bei hochgeklappter Plattform",
          "Fläche an den Haltepunkten für Auffahren und Wenden",
          "Höhenunterschied und Länge der Fahrstrecke",
          "Befestigungsmöglichkeit an Treppe, Wand oder Fundament",
          "Türen und Schwellen im Anschluss an die Plattform",
        ],
      },
      {
        h2: "Plattformlift oder andere Lösung",
        paragraphs: [
          "Für Personen, die sich setzen und wieder aufstehen können, ist ein Treppenlift mit Sitz meist die einfachere und platzsparendere Lösung. Sollen mehrere Geschosse komfortabel erschlossen werden und ist Platz vorhanden, kommt ein Homelift oder ein nachträglich eingebauter Aufzug infrage.",
        ],
      },
      {
        h2: "Barrierefreiheit im ganzen Weg denken",
        paragraphs: [
          "Ein Plattformlift löst einen Höhenunterschied, nicht den gesamten Zugang. Prüfen Sie den Weg vom Parkplatz oder Trottoir bis zum Ziel: Schwellen, einzelne Stufen, schmale Türen und die Bedienbarkeit der Steuerung entscheiden mit darüber, ob die Lösung im Alltag trägt.",
        ],
      },
      {
        h2: "Was Anbieter für eine Einschätzung benötigen",
        bullets: [
          "Standort und Postleitzahl",
          "Innen- oder Aussenbereich",
          "Höhenunterschied und Anzahl Stufen",
          "Treppenbreite und Treppenverlauf, gerade oder mit Kurven",
          "Nutzung: Rollstuhl, Rollator, Begleitperson, Transporte",
          "Fotos der Treppe oder des Zugangs",
          "Eigentumsverhältnisse, etwa Stockwerkeigentum oder Mietobjekt",
        ],
      },
      {
        h2: "Ablauf der Anfrage",
        paragraphs: [
          "Sie beschreiben Situation, Nutzung und Standort online. Registrierte Anbieter sehen die Anfrage im geschützten Portal und melden sich direkt bei Ihnen. Das Aufmass erfolgt vor Ort, weil Schienenverlauf, Befestigung und Platzverhältnisse nicht aus der Ferne beurteilt werden können.",
        ],
      },
    ],
    faq: [
      {
        q: "Was ist der Unterschied zwischen Plattformlift und Rollstuhllift?",
        a: "Die Begriffe werden meist gleichbedeutend verwendet. Gemeint ist ein Lift mit befahrbarer Plattform, der für die Rollstuhlnutzung ausgelegt ist, entweder entlang einer Treppe oder senkrecht.",
      },
      {
        q: "Braucht ein Plattformlift eine Bewilligung?",
        a: "Das hängt von Standort und Eingriff ab. Bei Anlagen an der Fassade, im öffentlichen Zugang oder bei Stockwerkeigentum sind Bewilligungen oder Beschlüsse üblich. Die Anbieter kennen die Anforderungen in Ihrer Region.",
      },
      {
        q: "Passt ein Plattformlift auf eine schmale Treppe?",
        a: "Nicht immer. Ein Plattformlift braucht mehr Treppenbreite als ein Sitzlift, weil die Plattform breiter ist. Die verbleibende Durchgangsbreite beurteilt der Anbieter beim Aufmass.",
      },
    ],
    related: [
      { to: "/treppenlift-schweiz", label: "Treppenlift Schweiz" },
      { to: "/lift-nachtraeglich-einbauen", label: "Lift nachträglich einbauen" },
      { to: "/lift-planen", label: "Lift planen" },
      { to: "/liftanbieter-schweiz", label: "Liftanbieter Schweiz" },
    ],
  },
];

/** Zusammengelegte und alte Seiten: Weiterleitung auf die passende Nachfolgeseite. */
export const SEO_REDIRECTS: { from: string; to: string }[] = [
  { from: "/liftanfrage-schweiz", to: "/liftanbieter-schweiz" },
  { from: "/aufzug-anfrage-schweiz", to: "/liftbau-schweiz" },
  { from: "/aufzugswartung-schweiz", to: "/liftservice-schweiz" },
  { from: "/leistungen/liftwartung", to: "/liftservice-schweiz" },
  { from: "/leistungen/liftreparatur", to: "/liftreparatur-schweiz" },
  { from: "/leistungen/liftmodernisierung", to: "/liftmodernisierung-schweiz" },
];

/** Hauptthemen für Startseite und Footer, mit beschreibenden Linktexten. */
export const TOPIC_LINKS: (SeoLink & { note: string })[] = [
  { to: "/liftbau-schweiz", label: "Liftbau in der Schweiz", note: "Neubau, Ersatz und Nachrüstung: Ablauf, Schacht und nötige Angaben." },
  { to: "/aufzug-kosten-schweiz", label: "Aufzugskosten verstehen", note: "Welche Faktoren den Preis bestimmen, inklusive Lift im Einfamilienhaus." },
  { to: "/lift-planen", label: "Liftprojekt planen", note: "Was Sie vor der Anfrage über Gebäude, Nutzung und Platz klären sollten." },
  { to: "/treppenlift-schweiz", label: "Treppenlift Anbieter finden", note: "Gerade und kurvige Treppen, Sitzlift oder Plattformlift, innen und aussen." },
  { to: "/liftservice-schweiz", label: "Liftservice anfragen", note: "Wartungsvertrag, Reparatur oder Wechsel der Wartungsfirma." },
  { to: "/liftmodernisierung-schweiz", label: "Liftmodernisierung planen", note: "Steuerung, Türen, Kabine, Sicherheit und die Frage Ersatz oder Umbau." },
  { to: "/lift-abo-schweiz", label: "Lift-Abo und Wartungsvertrag", note: "Was ein Wartungsvertrag enthält, Laufzeit, Kündigung und Anbieterwechsel." },
  { to: "/liftreparatur-schweiz", label: "Lift reparieren lassen", note: "Typische Störungen und welche Angaben ein Servicetechniker benötigt." },
  { to: "/personenaufzug-schweiz", label: "Personenaufzug in der Schweiz", note: "Haltestellen, Tragfähigkeit, Kabine und Schacht in Wohn- und Geschäftsbauten." },
  { to: "/lift-einfamilienhaus-schweiz", label: "Lift im Einfamilienhaus", note: "Homelift oder Personenaufzug, Platzbedarf und nachträglicher Einbau." },
  { to: "/homelift-schweiz", label: "Homelift Schweiz", note: "Kompakte Aufzüge für private Wohnhäuser, Platzbedarf und Ausführung." },
  { to: "/lift-nachtraeglich-einbauen", label: "Lift nachträglich einbauen", note: "Nachrüstung im Bestand: Machbarkeit, Standort und bauliche Abklärungen." },
  { to: "/liftanbieter-schweiz", label: "Liftanbieter Schweiz", note: "Wie Sie passende Liftfirmen für Ihr Projekt in der Schweiz finden." },
];

export function getSeoPage(slug: string): SeoPage | undefined {
  return SEO_PAGES.find((p) => p.slug === slug);
}
